// @flow

import React, {Component, createRef} from 'react';
import {debounce, isEqual, isObject, noop, throttle} from 'lodash';
import detectPassiveEvents from 'detect-passive-events';
import {DraggableCore} from 'react-draggable';
import {keyCodes} from '@pb/kit-util-key-codes';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './scrollable.component.module.styl';
import {getContainerSize} from './utils';
import {ScrollLine} from './scroll-line';
import {
    Direction,
    CANCEL_SCROLLABLE,
    SETTINGS,
    Style,
    DELTA,
    CUSTOM_SCROLL_EVENT,
    ONWHELL_THROTTLE_TIME
} from './constants';
import {ScrollArrows} from './scroll-arrows';
import {exceedGap} from './utils/exceedGap';

import type {Node} from 'react';
import type {DraggableData} from 'react-draggable';

import type {
    ScrollArrowsData,
    RenderCustomArrows,
    OnArrowScrollData,
    GetExternalScrollHandler,
    ExternalScrollHandler,
    GetOnWheelHandler,
    OnWheelHandler
} from './typedef';
import type {DirectionType, StyleType} from './constants';


const hasPassiveEventSupport = detectPassiveEvents.hasSupport;
const classNames = withCssModule(styles);

type DebounceReturnType = $Call<typeof debounce, Function>;
type ThrottleReturnType = $Call<typeof throttle, Function>;

export type ScrollSize = {|
    containerSize?: number,
    contentSize?: number,
|};

export type ScrollableData = {|
    shift: number,
    position: number,
    frozenShift: number,

    dragging: boolean,
    isArrowScrolling: boolean,
    isDataExternal: boolean,
|};

type UpdateData = {|
    shift: number,
    isArrowScrolling?: boolean,
    isAnimatedScrolling?: boolean,
    dragging?: boolean,
    position?: number,
|};

type State = {|
    dragging: boolean,
    shift: number,
    isScrolling: boolean,
    isArrowScrolling: boolean,
    isAnimatedScrolling: boolean,
    frozenShift: number,
    pointerPosition: {|
        clientY: number,
        clientX: number,
    |},
|};

type CommonProps = {|
    mix?: string,
    containerMix?: string,
    type?: $PropertyType<ScrollableTransitiveProps, 'type'>,
    scrolling?: $PropertyType<ScrollableTransitiveProps, 'scrolling'>,
    step?: $PropertyType<ScrollableTransitiveProps, 'step'>,
    shift?: $PropertyType<ScrollableTransitiveProps, 'shift'>,
    onDrag?: $PropertyType<ScrollableTransitiveProps, 'onDrag'>,
    onStartScroll?: $PropertyType<ScrollableTransitiveProps, 'onStartScroll'>,
    onStopScroll?: $PropertyType<ScrollableTransitiveProps, 'onStopScroll'>,
    scrollLineOption?: $PropertyType<ScrollableTransitiveProps, 'scrollLineOption'>,
    scrollArrows?: $PropertyType<ScrollableTransitiveProps, 'scrollArrows'>,
    renderCustomArrows?: $PropertyType<ScrollableTransitiveProps, 'renderCustomArrows'>,
    updateScrollableData?: $PropertyType<ScrollableTransitiveProps, 'updateScrollableData'>,
    getScrollable?: $PropertyType<ScrollableTransitiveProps, 'getScrollable'>,
    getExternalScrollHandler?: $PropertyType<ScrollableTransitiveProps, 'getExternalScrollHandler'>,
    getOnWheelHandler?: $PropertyType<ScrollableTransitiveProps, 'getOnWheelHandler'>,
    children: Node,
    externalContentScrolling: $NonMaybeType<$PropertyType<ScrollableTransitiveProps, 'externalContentScrolling'>>,
    direction: $NonMaybeType<$PropertyType<ScrollableTransitiveProps, 'direction'>>,
    hideScrollLine?: $PropertyType<ScrollableTransitiveProps, 'hideScrollLine'>,
    horizontalWheel: $PropertyType<ScrollableTransitiveProps, 'horizontalWheel'>,
    useMarginShiftToScroll: $PropertyType<ScrollableTransitiveProps, 'useMarginShiftToScroll'>,
    applyShiftWithAnimation: $PropertyType<ScrollableTransitiveProps, 'applyShiftWithAnimation'>,
    scrollableId: $PropertyType<ScrollableTransitiveProps, 'scrollableId'>,
    emitCustomEvent: $PropertyType<ScrollableTransitiveProps, 'emitCustomEvent'>,
    useMouseWheelEvent: $PropertyType<ScrollableTransitiveProps, 'useMouseWheelEvent'>,
    preventNativeScroll?: $PropertyType<ScrollableTransitiveProps, 'preventNativeScroll'>,
|};

type SetScrollablePosition = (callback: (number) => void) => void;

type Props = {|
    ...CommonProps,
    absolute: boolean,
    shadow: boolean,
    position?: number,
    preventOnScroll: boolean,
    contentSize: number,
    containerSize: number,
    getScrollableContainer: (el: ?HTMLDivElement) => void,
    isScrollLineExtracted: boolean,
    setScrollablePosition: SetScrollablePosition,
|};

let offsetY = 0;

/**
 * @deprecated It will be removed in 6 months 09-15-2025. use `@pb/kit-component-native-scroll`
 * */
export class ScrollableCore extends Component<Props, State> {
    scrollable: HTMLDivElement | null;
    container: HTMLDivElement | null;
    mount: boolean;
    endScrolling: DebounceReturnType;
    onPrepareUpdateDataThrottle: ThrottleReturnType;
    nodeRef = createRef<HTMLDivElement | null>();

    static defaultProps = {
        absolute: false,
        direction: Direction.VERTICAL,
        getScrollableContainer: noop,
        shadow: false,
        type: Style.LIGHT,
        scrolling: false,
        mix: '',
        externalContentScrolling: true,
        isScrollLineExtracted: false,
        preventOnScroll: true,
        setScrollablePosition: noop,
        hideScrollLine: false,
        onDrag: noop,
        horizontalWheel: false,
        useMarginShiftToScroll: false,
        applyShiftWithAnimation: false,
        scrollableId: '',
        emitCustomEvent: false,
        useMouseWheelEvent: true,
        preventNativeScroll: false
    };

    constructor(props: Props) {
        super(props);
        const {position, shift} = props;

        const currentShift = typeof position === 'number' && !isNaN(this.props.position)
            ? this.applyPosition(position)
            : this.applyShift(shift || 0);

        this.state = {
            dragging: false,
            isScrolling: false,
            isArrowScrolling: false,
            isAnimatedScrolling: false,
            shift: currentShift,
            frozenShift: 0,
            pointerPosition: {
                clientY: 0,
                clientX: 0
            }
        };
        this.mount = false;
        this.endScrolling = debounce(this._endScrolling, 1000, {leading: false, trailing: true});
        this.onPrepareUpdateDataThrottle = throttle(this.onPrepareUpdateData, ONWHELL_THROTTLE_TIME);
    }

    componentDidMount() {
        this.addListeners();

        const {getExternalScrollHandler, setScrollablePosition, getOnWheelHandler} = this.props;
        this.mount = true;
        if (getExternalScrollHandler) {
            getExternalScrollHandler(this.externalScroll);
        }
        if (getOnWheelHandler) {
            getOnWheelHandler(this.onWheel);
        }
        setScrollablePosition(this.updateScrollPosition);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.shouldUpdate(this.state, prevState, ['shift'])) {
            if (!this.state.isScrolling) {
                this.setState({isScrolling: true});
            }
            this.endScrolling();
        } else {
            this.disableLinksDragging();
        }

        if (this.shouldUpdate(this.props, prevProps, ['position', 'containerSize', 'contentSize', 'shift'])) {
            let data;
            const isPosition = typeof this.props.position === 'number' && !isNaN(this.props.position);
            const isShift = typeof this.props.shift === 'number' && !isNaN(this.props.shift);

            if (!isPosition && isShift) {
                const shift = this.applyShift(this.props.shift || 0, this.props);
                data = {
                    shift,
                    isAnimatedScrolling: this.props.applyShiftWithAnimation,
                    position: this.calcPosition(this.props, shift)
                };
            } else {
                const shift = typeof this.props.position === 'number' && !isNaN(this.props.position)
                    ? this.applyPosition(this.props.position, this.props)
                    : this.calcShift(0, this.props);

                data = {
                    shift,
                    position: this.calcPosition(this.props, shift)
                };
            }

            this.updateData(data, true);
        }
    }

    componentWillUnmount() {
        this.mount = false;

        this.removeListeners();
        this.endScrolling.cancel();
    }

    addListeners() {
        const container = this.container;
        if (container) {
            if (this.props.useMouseWheelEvent) {
                container.addEventListener(
                    'wheel', this.onWheel, hasPassiveEventSupport ? {passive: false} : false
                );
            }
            container.addEventListener('pointermove', this.onPointerMove);
            container.addEventListener('pointerdown', this.onPointerDown);
        }
    }

    removeListeners() {
        const container = this.container;
        if (container) {
            if (this.props.useMouseWheelEvent) {
                container.removeEventListener('wheel', this.onWheel);
            }
            container.removeEventListener('pointermove', this.onPointerMove);
            container.removeEventListener('pointerdown', this.onPointerDown);
        }
    }

    onPointerDown = (e: PointerEvent) => {
        if (e.pointerType !== 'touch') {
            return;
        }

        this.setState({
            pointerPosition: {
                clientY: e.clientY,
                clientX: e.clientX
            }
        });
    };

    onPointerMove = (e: PointerEvent) => {
        if (e.pointerType !== 'touch') {
            return;
        }

        const deltaY = e.clientY - this.state.pointerPosition.clientY;
        const deltaX = e.clientX - this.state.pointerPosition.clientX;

        const correctedDeltaX = deltaX > 0 ? -Math.abs(deltaX) : Math.abs(deltaX);
        const correctedDeltaY = deltaY > 0 ? -Math.abs(deltaY) : Math.abs(deltaY);

        this.handleScrollEvent(correctedDeltaX, correctedDeltaY, e);

        this.setState({
            pointerPosition: {
                clientY: e.clientY,
                clientX: e.clientX
            }
        });
    };

    getScrollDelta(isDeltaNegative: boolean) {
        const {step: scrollStep, direction} = this.props;
        const step = scrollStep || SETTINGS[direction].step;
        const delta = isDeltaNegative ? -DELTA : DELTA;

        return delta * step;
    }

    onKeyDown = (e: KeyboardEvent) => {
        const {KEY_LEFT, KEY_RIGHT} = keyCodes;
        const isRightKey = e.key === KEY_RIGHT;
        if (e.key === KEY_LEFT || isRightKey) {
            const scrollDelta = this.getScrollDelta(isRightKey);
            this.onPrepareUpdateData(scrollDelta);
        }
    };

    shouldUpdate = (obj1: Object, obj2: Object, optionalFields: ?string[]): boolean => {
        if (optionalFields && Array.isArray(optionalFields) && optionalFields.length) {
            return optionalFields.some(field => (isObject(obj1[field])
                ? !isEqual(obj1[field], obj2[field])
                : !isNaN(obj1[field]) && !isNaN(obj2[field]) && obj1[field] !== obj2[field])
            );
        }

        return !isEqual(obj1, obj2);
    };

    updateScrollPosition = (position: number) => {
        const shift = this.applyPosition(position, this.props);

        const data = {
            shift,
            position: this.calcPosition(this.props, shift)
        };

        this.updateData(data, true);
    };

    _endScrolling = () => {
        this.setState({
            isScrolling: false
        });
    };

    disableLinksDragging = () => {
        if (this.scrollable) {
            [...this.scrollable.getElementsByTagName('a')].forEach(
                a => {
                    if (!a.dataset.dragging) {
                        a.dataset.dragging = 'disabled';
                        a.addEventListener('dragstart', (e: DragEvent) => e.preventDefault());
                    }
                }
            );
        }
    };

    getDelta = (deltaX: number, deltaY: number) => {
        const {horizontalWheel} = this.props;
        // TODO: try to use Object.is(deltaX, -0)
        // eslint-disable-next-line no-compare-neg-zero
        return horizontalWheel && deltaX !== -0 ? deltaX : deltaY;
    };

    onWheel: OnWheelHandler = e => {
        const deltaY = offsetY += e.deltaY;

        this.handleScrollEvent(e.deltaX, deltaY, e);
    };

    handleScrollEvent = (deltaX: number, deltaY: number, e: MouseEvent) => {
        if (!this.hasScrollLine(this.getBounds(this.props))) {
            return;
        }

        if (this.checkChildScroll(e)) {
            return;
        }

        e.preventDefault();

        // eslint-disable-next-line no-compare-neg-zero
        if (!this.props.horizontalWheel && deltaY === -0) {
            return;
        }

        const {shift} = this.state;
        const {direction, preventOnScroll} = this.props;
        const isDeltaNegative = this.getDelta(deltaX, deltaY) > 0;
        const scrollDelta = this.getScrollDelta(isDeltaNegative);

        this.onPrepareUpdateDataThrottle(scrollDelta);
        const nextShift = this.calcShift(scrollDelta, this.props);
        if (direction === Direction.HORIZONTAL || preventOnScroll || shift - nextShift !== 0) {
            e.stopPropagation();
        }
    };

    externalScroll: ExternalScrollHandler = (delta: number) => {
        this.onPrepareUpdateData(-delta);
    };

    onClickCapture = (e: Event) => {
        if (this.state.dragging) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    onStart = (e: Event): ?boolean => {
        if (this.container) {
            this.container.dispatchEvent(new Event('scrollable:start', {bubbles: true}));
        }

        const {onStartScroll} = this.props;
        if (onStartScroll) {
            return onStartScroll(e);
        }

        return null;
    };

    onDrag = (e: Event, data: DraggableData) => {
        if (this.checkChildScroll(e)) {
            return;
        }
        e.stopPropagation();
        const {direction, onDrag} = this.props;

        const {axis, delta, lastAxis} = SETTINGS[direction];
        if (data[axis] !== data[lastAxis]) {
            this.onPrepareUpdateData(data[delta], true);
        }

        if (onDrag) {
            onDrag();
        }
    };

    onStop = (e: Event, data: DraggableData) => {
        const {direction, onStopScroll} = this.props;
        const {delta} = SETTINGS[direction];

        if (onStopScroll) {
            onStopScroll();
        }

        setTimeout(() => {
            if (this.mount) {
                this.onPrepareUpdateData(data[delta]);
            }
        }, 0);
    };

    onPrepareUpdateData = (delta: number, dragging: boolean = false) => {
        offsetY = 0;

        const data = {dragging, shift: this.calcShift(delta, this.props)};
        this.updateData(data);
    };

    updateData(data: UpdateData, isDataExternal: boolean = false) {
        const {updateScrollableData, emitCustomEvent} = this.props;
        const {shift, position, dragging = false, isArrowScrolling = false, isAnimatedScrolling = false} = data;

        if (shift !== this.state.shift ||
            dragging !== this.state.dragging ||
            isArrowScrolling !== this.state.isArrowScrolling) {

            const {shift: nextFrozenShift, frozenShift} = this.state;

            const stateData = {
                shift,
                dragging,
                isArrowScrolling,
                frozenShift: isArrowScrolling && !this.state.isArrowScrolling ? nextFrozenShift : frozenShift
            };

            const nextState = isDataExternal ? {shift} : stateData;
            this.setState({...nextState, isAnimatedScrolling});

            if (emitCustomEvent && this.scrollable) {
                this.scrollable.dispatchEvent(new Event(CUSTOM_SCROLL_EVENT, {bubbles: true}));
            }

            if (updateScrollableData) {
                updateScrollableData({
                    ...stateData,
                    isDataExternal,
                    position: position !== undefined ? position : this.calcPosition(this.props, shift)
                });
            }
        }
    }

    checkChildScroll(e: Event) {
        const childElement = document.activeElement;
        return childElement && childElement.clientHeight < childElement.scrollHeight && e.target === childElement;
    }

    getBounds(props: Props) {
        const {contentSize, containerSize} = props;
        const from = containerSize - contentSize;
        return {from: Math.min(from, 0), to: 0};
    }

    applyShift = (shift: number, props?: Props) => {
        return this.calcShift(shift, props || this.props, false);
    };

    applyPosition = (position: number, props?: Props) => {
        const {from} = this.getBounds(props ? props : this.props);
        return position > 0 ? from * (position > 1 ? 1 : position) : 0;
    };

    calcPosition = (props: Props, shift: number) => {
        const {from} = this.getBounds(props);
        return from === 0 ? from : Math.abs(shift / from);
    };

    calcShift(delta: number, props: Props, addValue: boolean = true) {
        const {from, to} = this.getBounds(props);
        let nextShift = addValue ? this.state.shift + delta : delta;
        if (nextShift < from) {
            nextShift = from;
        } else if (nextShift > to) {
            nextShift = to;
        }
        return Math.round(nextShift);
    }

    getScrollable = (el: HTMLDivElement | null) => {
        this.scrollable = el;
        if (this.props.getScrollable) {
            this.props.getScrollable(el);
        }
        this.disableLinksDragging();
    };

    onArrowScroll = (data: OnArrowScrollData) => {
        const {shift, isArrowScrolling} = data;

        this.updateData({
            isArrowScrolling,
            shift: this.calcShift(shift, this.props, false)
        });
    };

    onNativeScroll = (e: any) => {
        if (e.target.scroll) {
            e.target.scroll(0, 0);
        }
    };

    onContainerRef = (container: HTMLDivElement | null) => {
        this.container = container;
        this.props.getScrollableContainer(container);
        this.nodeRef.current = container;
    };

    hasScrollLine({from, to}: { from: number, to: number, }) {
        return from !== to;
    }

    render() {
        const {shift, frozenShift, isArrowScrolling, isAnimatedScrolling, dragging, isScrolling} = this.state;
        const {from, to} = this.getBounds(this.props);
        const {
            direction,
            absolute,
            contentSize,
            containerSize,
            shadow,
            type,
            mix,
            containerMix,
            scrollArrows,
            scrollLineOption,
            scrolling: showOnlyOnScroll,
            externalContentScrolling,
            isScrollLineExtracted,
            renderCustomArrows,
            useMarginShiftToScroll,
            hideScrollLine,
            scrollableId,
            preventNativeScroll
        } = this.props;

        const {indent, margin} = SETTINGS[direction];
        const hasScrollLine = this.hasScrollLine({from, to});
        const hasArrows = !!scrollArrows;
        const isVertical = direction === Direction.VERTICAL;

        const shadowClasses = hasScrollLine && shadow ? {
            'scrollable_start': shift < to,
            'scrollable_end': shift > from,
            'scrollable_shadow-light': type === Style.LIGHT,
            'scrollable_shadow-dark': type === Style.DARK
        } : null;

        const scrollableClasses = classNames('scrollable', {
            'scrollable_enabled': hasScrollLine,
            'scrollable_horizontal': !isVertical,
            'scrollable_vertical': isVertical,
            'scrollable_absolute': absolute,
            'scrollable_with-scroll-line': hasScrollLine && !isScrollLineExtracted,
            'scrollable_with-arrows': hasArrows
        }, mix, shadowClasses);

        const containerClasses = classNames('scrollable__container', containerMix, {
            scrollable__container_moving: isArrowScrolling || isAnimatedScrolling
        });

        const scrollableStyle = isVertical && containerSize && hasScrollLine ? {
            maxHeight: containerSize
        } : null;

        let scrollableContainerStyle = null;
        if (!externalContentScrolling) {
            const cssProp = useMarginShiftToScroll ? margin : indent;
            scrollableContainerStyle = {
                [cssProp]: shift
            };
        } else if (externalContentScrolling && isArrowScrolling) {
            scrollableContainerStyle = {
                [margin]: shift - frozenShift
            };
        }

        return (
            <div
                {...scrollableId ? {id: scrollableId} : null}
                ref={this.getScrollable}
                className={scrollableClasses}
                style={scrollableStyle}
                onScroll={preventNativeScroll ? this.onNativeScroll : noop}>
                {renderCustomArrows ? renderCustomArrows({parent: this.scrollable, scrollArrows}) : null}
                {!renderCustomArrows && hasArrows && hasScrollLine && !isVertical ? (
                    <ScrollArrows
                        parent={this.scrollable}
                        {...{
                            isLeftArrow: shift !== to,
                            isRightArrow: shift !== from,
                            shift,

                            contentSize,
                            containerSize,
                            scrollArrows,

                            onArrowScroll: this.onArrowScroll
                        }}
                    />
                ) : null}

                <DraggableCore
                    nodeRef={this.nodeRef}
                    onStart={this.onStart}
                    onDrag={this.onDrag}
                    onStop={this.onStop}
                    enableUserSelectHack={false}
                    allowMobileScroll={true}
                    cancel={`.${CANCEL_SCROLLABLE}`}>
                    <div
                        style={scrollableContainerStyle}
                        ref={this.onContainerRef}
                        className={containerClasses}
                        tabIndex={-1}
                        onClickCapture={this.onClickCapture}
                        onKeyDown={!isVertical ? this.onKeyDown : null}>
                        {this.props.children}
                    </div>
                </DraggableCore>
                {!hideScrollLine && hasScrollLine ? (
                    <ScrollLine
                        {...{
                            ...scrollLineOption,
                            shift,
                            type,
                            direction,
                            contentSize,
                            containerSize,
                            show: !showOnlyOnScroll || dragging || isScrolling,
                            updateData: this.onPrepareUpdateData
                        }}
                    />
                ) : null}
            </div>
        );
    }
}

type ScrollableState = {|
    containerSize: number,
    contentSize: number,
|};

/**
 *  @deprecated It will be removed in 6 months 09-15-2025. use `@pb/kit-component-native-scroll`
 *  */
export type ScrollableTransitiveProps = {|
    absolute?: boolean,
    shadow?: boolean,
    position?: number,
    onScrollableSizeChange?: (scrollArea: ScrollSize) => void,
    isScrollLineExtracted?: boolean,
    preventOnScroll?: boolean,
    renderCustomArrows?: RenderCustomArrows,
    setScrollablePosition?: SetScrollablePosition,

    type?: StyleType,
    scrolling?: boolean, // TODO - change prop name to "showOnlyOnScroll" showLineOnScrolling
    step?: number,
    shift?: number,
    onDrag?: () => void,
    onStartScroll?: (e: Event) => ?boolean,
    onStopScroll?: () => void,
    scrollLineOption?: {|
        id: string,
        getRef: (el: ?HTMLDivElement) => void,
    |},
    scrollArrows?: ScrollArrowsData,
    updateScrollableData?: (data: ScrollableData) => void,
    getScrollable?: (el: ?HTMLDivElement) => void,
    getExternalScrollHandler?: GetExternalScrollHandler,
    getOnWheelHandler?: GetOnWheelHandler,
    externalContentScrolling?: boolean,
    direction?: DirectionType,
    hideScrollLine?: boolean,
    horizontalWheel?: boolean,
    useMarginShiftToScroll?: boolean,
    applyShiftWithAnimation?: boolean,
    scrollableId?: string,
    emitCustomEvent?: boolean,
    useMouseWheelEvent?: boolean,
    preventNativeScroll?: boolean,
|};

export type ScrollableProps = {|
    ...CommonProps,
    absolute?: $PropertyType<ScrollableTransitiveProps, 'absolute'>,
    shadow?: $PropertyType<ScrollableTransitiveProps, 'shadow'>,
    position?: $PropertyType<ScrollableTransitiveProps, 'position'>,
    onScrollableSizeChange?: $PropertyType<ScrollableTransitiveProps, 'onScrollableSizeChange'>,
    isScrollLineExtracted?: $PropertyType<ScrollableTransitiveProps, 'isScrollLineExtracted'>,
    preventOnScroll?: $PropertyType<ScrollableTransitiveProps, 'preventOnScroll'>,
    setScrollablePosition?: $PropertyType<ScrollableTransitiveProps, 'setScrollablePosition'>,
|};

/**
 *  @deprecated It will be removed in 6 months 09-15-2025. use `@pb/kit-component-native-scroll`
 *  */
export class Scrollable extends Component<ScrollableProps, ScrollableState> {
    scrollable: ?HTMLDivElement;
    scrollableContainer: ?HTMLDivElement;
    position: ?number;
    updatePosition: ?Function;
    scrollableObserver: ?ResizeObserver = null;
    delayedUpdate: boolean = false;

    static defaultProps = {
        direction: Direction.VERTICAL,
        externalContentScrolling: false,
        horizontalWheel: false,
        useMarginShiftToScroll: false,
        applyShiftWithAnimation: false,
        scrollableId: '',
        emitCustomEvent: false,
        useMouseWheelEvent: true
    };

    state = {
        containerSize: 0,
        contentSize: 0
    };

    componentDidMount() {
        const {setScrollablePosition} = this.props;

        if (setScrollablePosition) {
            setScrollablePosition(this.setPosition);
        }
        this.updateSizes();

        window.addEventListener('resize', this.updateSizes);

        if (this.scrollable && this.scrollableContainer) {
            this.scrollableObserver = new ResizeObserver(() => requestAnimationFrame(this.updateSizes));
            this.scrollableObserver.observe(this.scrollable);
            if (this.scrollableObserver && this.scrollableContainer) {
                this.scrollableObserver.observe(this.scrollableContainer);
            }
        }
    }

    componentDidUpdate() {
        this.updateSizes();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateSizes);
        if (this.scrollableObserver) {
            this.scrollableObserver.disconnect();
        }
    }

    updateSizes = () => {
        const scrollable = this.scrollable;
        const scrollableContainer = this.scrollableContainer;

        if (scrollable && scrollableContainer) {
            const {direction, onScrollableSizeChange} = this.props;
            const {offsetSize, size} = SETTINGS[direction];
            // $FlowFixMe TODO: an indexer property is missing in HTMLDivElement
            const contentSize = Math.round(scrollableContainer.getBoundingClientRect()[size]);
            const containerSize = getContainerSize({
                contentSize,
                node: scrollable,
                direction,
                offsetSize
            });

            if (!isNaN(containerSize) && !isNaN(contentSize)
                && (exceedGap(this.state.containerSize, containerSize) || contentSize !== this.state.contentSize)) {

                this.setState({
                    containerSize,
                    contentSize
                }, () => {
                    if (this.delayedUpdate && this.updatePosition && this.position) {
                        this.updatePosition(this.position);
                    }
                });
                if (onScrollableSizeChange) {
                    onScrollableSizeChange({
                        containerSize
                    });
                }
            }
        }
    };

    getScrollable = (el: ?HTMLDivElement) => {
        const {getScrollable} = this.props;
        this.scrollable = el;
        if (getScrollable) {
            getScrollable(el);
        }
    };

    getScrollableContainer = (ref: ?HTMLDivElement) => {
        this.scrollableContainer = ref;
    };

    setScrollableCallback = (callback: Function) => {
        this.updatePosition = callback;
    };

    setPosition = (position: number) => {
        if (this.updatePosition) {
            if (this.state.containerSize !== 0 && this.state.contentSize !== 0) {
                this.updatePosition(position);
                this.delayedUpdate = false;
                this.position = null;
            } else {
                this.delayedUpdate = true; // callback should be triggered if state sizes not 0
                this.position = position;
            }
        }
    };

    render() {
        const {containerSize, contentSize} = this.state;
        const {
            mix,
            containerMix,
            shadow,
            type,
            absolute,
            scrollArrows,
            scrolling,
            step,
            onDrag,
            onStartScroll,
            onStopScroll,
            position,
            shift,
            updateScrollableData,
            getExternalScrollHandler,
            getOnWheelHandler,
            externalContentScrolling,
            children,
            direction,
            scrollLineOption,
            renderCustomArrows,
            preventOnScroll,
            isScrollLineExtracted,
            hideScrollLine,
            horizontalWheel,
            useMarginShiftToScroll,
            applyShiftWithAnimation,
            scrollableId,
            emitCustomEvent,
            useMouseWheelEvent,
            preventNativeScroll
        } = this.props;

        return (
            <ScrollableCore
                {...{
                    mix,
                    containerMix,
                    shadow,
                    type,
                    absolute,
                    scrollArrows,
                    scrolling,
                    step,
                    onDrag,
                    onStartScroll,
                    onStopScroll,
                    position,
                    shift,
                    updateScrollableData,
                    getExternalScrollHandler,
                    getOnWheelHandler,
                    externalContentScrolling,
                    direction,
                    containerSize,
                    contentSize,
                    scrollLineOption,
                    renderCustomArrows,
                    preventOnScroll,
                    isScrollLineExtracted,
                    setScrollablePosition: this.setScrollableCallback,
                    hideScrollLine,
                    useMarginShiftToScroll,
                    applyShiftWithAnimation,
                    horizontalWheel,
                    scrollableId,
                    emitCustomEvent,
                    useMouseWheelEvent,
                    preventNativeScroll
                }}
                getScrollable={this.getScrollable}
                getScrollableContainer={this.getScrollableContainer}>
                {children}
            </ScrollableCore>
        );
    }
}

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./typedef");

require("./constants");
